import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  FormControlLabel,
  FormHelperText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { IconChevronDown, IconWand } from '@tabler/icons-react';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import { Control, Controller } from 'react-hook-form';

import CustomSwitch from '@shared/components/forms/theme-elements/CustomSwitch';

interface SpeechPacingFieldsProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  disabled?: boolean;
  collapsible?: boolean;
  onApplyDefaults?: () => void;
}

const parseNumber = (raw: string): number | undefined => (raw === '' ? undefined : Number(raw));

export const SpeechPacingFields: React.FC<SpeechPacingFieldsProps> = ({
  control,
  disabled = false,
  collapsible = true,
  onApplyDefaults,
}) => {
  const { t } = useTranslation();

  const numberField = (name: string, labelKey: string) => (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <TextField
          fullWidth
          type="number"
          disabled={disabled}
          inputProps={{ min: 0, step: '10' }}
          label={t(labelKey)}
          value={field.value ?? ''}
          onChange={(e) => field.onChange(parseNumber(e.target.value))}
          variant="outlined"
        />
      )}
    />
  );

  const body = (
    <>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('assistantsPage.modals.speechPacing.description')}
        </Typography>
        {onApplyDefaults && !disabled && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<IconWand size={16} />}
            onClick={onApplyDefaults}
            sx={{ flexShrink: 0 }}>
            {t('assistantsPage.modals.applyDefaults')}
          </Button>
        )}
      </Stack>

      <Box>
        <Controller
          name="speechPacing.enabled"
          control={control}
          render={({ field }) => (
            <Box>
              <FormControlLabel
                control={
                  <CustomSwitch
                    sx={{ ml: '20px', mr: '14px' }}
                    checked={!!field.value}
                    disabled={disabled}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      field.onChange(e.target.checked)
                    }
                  />
                }
                label={t('assistantsPage.modals.speechPacing.enabled')}
              />
              <FormHelperText sx={{ ml: '20px' }}>
                {t('assistantsPage.modals.speechPacing.enabledHelp')}
              </FormHelperText>
            </Box>
          )}
        />
      </Box>

      <Box sx={{ mt: 1 }}>
        <Controller
          name="speechPacing.seed"
          control={control}
          render={({ field }) => (
            <TextField
              fullWidth
              disabled={disabled}
              label={t('assistantsPage.modals.speechPacing.seed')}
              helperText={t('assistantsPage.modals.speechPacing.seedHelp')}
              value={field.value ?? ''}
              onChange={(e) => field.onChange(e.target.value === '' ? undefined : e.target.value)}
              variant="outlined"
            />
          )}
        />
      </Box>

      <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
        {t('assistantsPage.modals.speechPacing.phraseBoundaryDelayMs.title')}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {t('assistantsPage.modals.speechPacing.phraseBoundaryDelayMs.help')}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
        {numberField(
          'speechPacing.phraseBoundaryDelayMs.minMs',
          'assistantsPage.modals.speechPacing.minMs',
        )}
        {numberField(
          'speechPacing.phraseBoundaryDelayMs.maxMs',
          'assistantsPage.modals.speechPacing.maxMs',
        )}
      </Box>

      <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
        {t('assistantsPage.modals.speechPacing.sentenceBoundaryDelayMs.title')}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {t('assistantsPage.modals.speechPacing.sentenceBoundaryDelayMs.help')}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
        {numberField(
          'speechPacing.sentenceBoundaryDelayMs.minMs',
          'assistantsPage.modals.speechPacing.minMs',
        )}
        {numberField(
          'speechPacing.sentenceBoundaryDelayMs.maxMs',
          'assistantsPage.modals.speechPacing.maxMs',
        )}
      </Box>
    </>
  );

  if (!collapsible) {
    return <Box>{body}</Box>;
  }

  return (
    <Accordion disableGutters>
      <AccordionSummary expandIcon={<IconChevronDown />}>
        <Typography variant="subtitle2">{t('assistantsPage.modals.speechPacing.title')}</Typography>
      </AccordionSummary>
      <AccordionDetails>{body}</AccordionDetails>
    </Accordion>
  );
};
